import { useState, useCallback, useMemo } from 'react';
import { useDevice, useSensorData, useProductCapabilities, useFieldPending, useMTCommand } from '@/hooks';
import { deviceService } from '@/api/services';
import { NAMTMessageType } from '@/types/enums';
import { resolveGeoAutoArm } from '@/lib/enum-resolvers';
import {
  findPendingMT,
  SIREN_SYNC_OPTIONS,
  toOrdinal,
  LEGACY_SIREN_SYNC_STRING_MAP,
  GEO_AUTO_ARM_OPTIONS,
  LEGACY_GEO_AUTO_ARM_STRING_MAP,
} from '@/lib/sensor-commands';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { ToggleSwitch, SectionCard, SettingRow, SelectDropdown, PendingBadge } from '@/components/common/ConfigControls';
import { DeviceZoneSettings } from './DeviceZoneSettings';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
const TABS = [
  { key: 'system', label: 'System Settings' },
  { key: 'geofence', label: 'Geofence Sync' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function ConfigurationPage() {
  const { selectedDeviceInfo, selectedDevice, deviceInfoLoading, refreshDeviceInfo } = useDevice();
  const { sensorData } = useSensorData();
  const caps = useProductCapabilities();
  const deviceUUID = selectedDevice?.deviceProperties.deviceUUID ?? '';
  const globalSettings = selectedDeviceInfo?.globalSettings;
  // sendCommand: send + register with usePendingMTTracking + refetch, all in
  // one — see the hook's own doc comment. mtObjList is the reconciled list
  // (pending immediately after sending, held through the post-confirmation
  // grace period), not the raw selectedDeviceInfo.mtObjList.
  const { sendCommand, mtObjList, feedback, setFeedback } = useMTCommand(deviceUUID);

  const [activeTab, setActiveTab] = useState<TabKey>('system');
  // Immediate optimistic value + pending flag per field, shared across every
  // control on this page — see the hook's doc comment for why a control's
  // display value can't be derived purely from mtObjList/globalSettings.
  const fieldPending = useFieldPending();

  // Security zones — also used by the Sensors page's own instance for xPulse
  // zone slots; each page keeps its own copy since neither shares state.
  const securityZones = useMemo(() => sensorData?.securitySensorlist ?? [], [sensorData]);

  // ─── Zone Type Category (REST, not MT) ───
  // Mirrors zoneCategoryChangedCallback in XPulseSettingsPageViewModel.cs /
  // DeviceConfigPageViewModel.cs exactly, including the specific oddity that it
  // looks up the target security-zone record by `sourceAddressLSB === 1` rather
  // than the actual sensor's own LSB.
  //
  // IMPORTANT caveat found while debugging this: the backend
  // (DevicePropertiesHandlerV2.cs, setDevicePropertiesResponse) sets
  // `httpStatusCode = OK` UNCONDITIONALLY before it even attempts the write, and
  // never changes it if the inner `dd.getSecurityProperties(...)` lookup fails —
  // so a 200 response here does NOT mean the value actually changed server-side.
  // The one failure mode this client CAN detect is "no matching LSB===1 zone
  // record exists yet for this zoneNum" (checked below) — which will always be
  // the case for an xPulse wireless sensor's zones unless the device's built-in
  // unit separately reports a mirrored LSB===1 entry for that same zoneNum. This
  // may be a structural limitation shared with mobile's own XPulseSettingsPage
  // (same lookup, same address filter) rather than a portal-only bug — flagging
  // rather than asserting, per feedback_dont_assert_bugs_from_static_analysis.
  const handleSetZoneCategory = useCallback(
    async (zoneNum: number, deviceCategory: number) => {
      const zoneRecord = securityZones.find((z) => z.zoneNum === zoneNum && z.sourceAddressLSB === 1);
      if (!zoneRecord) {
        setFeedback({
          type: 'error',
          message: `No matching zone record (address LSB 1) found for zone ${zoneNum} yet — the category can't be changed until the device reports one.`,
        });
        return;
      }
      try {
        await deviceService.setDeviceProperties({
          deviceUUID,
          containedObj: {
            sourceAddressMSB: zoneRecord.sourceAddressMSB,
            sourceAddressLSB: zoneRecord.sourceAddressLSB,
            zoneInstance: zoneRecord.zoneNum - 1,
            deviceCategory,
          },
        });
        setFeedback({ type: 'success', message: 'Zone category update sent.' });
      } catch {
        setFeedback({ type: 'error', message: 'Failed to update zone category.' });
        return;
      }
      // This is a synchronous REST write, not a queued MT — there's nothing to
      // poll for, so force an immediate refetch or the change won't be visible
      // until the next 5s poll. Mirrors mobile's _refreshView.TriggerRefresh(true).
      await refreshDeviceInfo();
    },
    [securityZones, deviceUUID, refreshDeviceInfo, setFeedback],
  );

  // ─── Loading / empty states ───

  if (deviceInfoLoading) return <PageSpinner />;

  if (!globalSettings) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Configuration</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage device settings and preferences</p>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-navy-600 dark:bg-navy-900">
          <p className="text-sm text-slate-400 dark:text-slate-500">No device selected or settings unavailable.</p>
        </div>
      </div>
    );
  }

  // ─── Render helpers for each tab ───

  const renderSystemSettings = () => {
    const sirenSyncOrdinal =
      fieldPending.getOptimisticValue<number>('remoteSirenSyncMode') ??
      toOrdinal(globalSettings.remoteSirenSyncMode, LEGACY_SIREN_SYNC_STRING_MAP) ??
      0;

    return (
    <div className="space-y-6">
      <SectionCard title="System Settings">
        <div className="divide-y divide-slate-100 dark:divide-navy-800">
          <SettingRow label="Alerts Enabled" description="Enable or disable all alert notifications from the device.">
            <div className="flex items-center">
              <ToggleSwitch
                enabled={fieldPending.getOptimisticValue<boolean>('alertsEnabled') ?? globalSettings.alertsEnabled}
                loading={false}
                onToggle={() =>
                  fieldPending.run('alertsEnabled', !globalSettings.alertsEnabled, () =>
                    sendCommand(globalSettings.alertsEnabled ? NAMTMessageType.SET_ALERTS_DISABLED : NAMTMessageType.SET_ALERTS_ENABLED),
                  )
                }
              />
              {(fieldPending.isPending('alertsEnabled') ||
                findPendingMT(mtObjList, NAMTMessageType.SET_ALERTS_ENABLED) ||
                findPendingMT(mtObjList, NAMTMessageType.SET_ALERTS_DISABLED)) && <PendingBadge />}
            </div>
          </SettingRow>
          <SettingRow label="Status Reporting" description="Enable periodic status reports from the device.">
            <div className="flex items-center">
              <ToggleSwitch
                enabled={fieldPending.getOptimisticValue<boolean>('statusReportingEnabled') ?? globalSettings.statusReportingEnabled}
                loading={false}
                onToggle={() =>
                  fieldPending.run('statusReportingEnabled', !globalSettings.statusReportingEnabled, () =>
                    sendCommand(globalSettings.statusReportingEnabled ? NAMTMessageType.SET_STATUS_DISABLED : NAMTMessageType.SET_STATUS_ENABLED),
                  )
                }
              />
              {(fieldPending.isPending('statusReportingEnabled') ||
                findPendingMT(mtObjList, NAMTMessageType.SET_STATUS_ENABLED) ||
                findPendingMT(mtObjList, NAMTMessageType.SET_STATUS_DISABLED)) && <PendingBadge />}
            </div>
          </SettingRow>
          <SettingRow label="Siren Enabled" description="Allow the onboard siren to sound during alerts.">
            {caps.showSirenSetting ? (
              <div className="flex items-center">
                <ToggleSwitch
                  enabled={fieldPending.getOptimisticValue<boolean>('sirenEnabled') ?? globalSettings.sirenEnabled}
                  loading={false}
                  onToggle={() =>
                    fieldPending.run('sirenEnabled', !globalSettings.sirenEnabled, () =>
                      sendCommand(globalSettings.sirenEnabled ? NAMTMessageType.SET_SIREN_DISABLED : NAMTMessageType.SET_SIREN_ENABLED),
                    )
                  }
                />
                {(fieldPending.isPending('sirenEnabled') ||
                  findPendingMT(mtObjList, NAMTMessageType.SET_SIREN_ENABLED) ||
                  findPendingMT(mtObjList, NAMTMessageType.SET_SIREN_DISABLED)) && <PendingBadge />}
              </div>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-500">Not available on this device</span>
            )}
          </SettingRow>
          <SettingRow label="Remote Siren Sync Mode" description="Control how wireless sirens synchronize.">
            <div className="flex items-center">
              <SelectDropdown
                value={String(sirenSyncOrdinal)}
                options={SIREN_SYNC_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                onChange={(val) =>
                  fieldPending.run('remoteSirenSyncMode', Number(val), () =>
                    sendCommand(NAMTMessageType.SET_WIRELESS_SIREN_SYNC_MODE, [val]),
                  )
                }
              />
              {(fieldPending.isPending('remoteSirenSyncMode') || findPendingMT(mtObjList, NAMTMessageType.SET_WIRELESS_SIREN_SYNC_MODE)) && (
                <PendingBadge />
              )}
            </div>
          </SettingRow>
        </div>
      </SectionCard>

      {/* Device's own built-in Zone 1-4 (sourceAddressLSB===1) — distinct from any
          wireless sensor's zones, mirrors DeviceConfigPageViewModel.refreshDeviceZones().
          Mobile gates this behind the same DeviceType===TRITON check as the wireless
          pairing section (RefreshModel() calls refreshSensorManagement() and
          refreshDeviceZones() together, both inside one `if (DeviceType != TRITON) return`) —
          not shown for Insight/VTracker. */}
      {caps.supportsWirelessExpansion && (
        <DeviceZoneSettings
          securityZones={securityZones}
          mtObjList={mtObjList}
          sendCommand={sendCommand}
          onSetZoneCategory={handleSetZoneCategory}
        />
      )}
    </div>
    );
  };

  const renderGeofenceSync = () => {
    // formatMTCommandPayloadByte (NAMTObject.cs) does Convert.ToByte(dataStrList[0])
    // in a try/catch that silently swallows the exception and writes 0 on
    // failure — a legacy string enum name (what this used to send) always
    // throws, so every selection except "Disabled" silently wrote Disabled
    // to the device. Mobile confirms the correct payload is the plain
    // numeric ordinal as a string (GeofencePageViewModel.cs's
    // geoAutoArmChangedCallback: `((int)newSetting.Key).ToString()`).
    // Pending MT command carries the new value in its own payload
    // (dataStrList[0]) — fall through to it once the local optimistic value
    // clears (right after the enqueue POST resolves) so the dropdown keeps
    // showing the new value for the whole time the command is actually
    // pending on the device (plus the post-confirmation grace period), not
    // just during the POST. See PENDING_STATE_PATTERN.md (repo root) for the
    // general shape of this fix. The "Current setting" summary text below is
    // intentionally NOT run through this fallback — it's meant to reflect
    // exactly what deviceInfo currently reports, unaffected by the MT queue.
    const pendingGeoAutoArmMT = findPendingMT(mtObjList, NAMTMessageType.MT_MESSAGE_SET_GEO_AUTO_ARM_DISARM_MODE);
    const confirmedGeoAutoArm = toOrdinal(globalSettings.geoAutoArmSetting, LEGACY_GEO_AUTO_ARM_STRING_MAP) ?? 0;
    const geoAutoArmValue =
      fieldPending.getOptimisticValue<number>('geoAutoArm') ??
      (pendingGeoAutoArmMT?.dataStrList?.[0] != null ? Number(pendingGeoAutoArmMT.dataStrList[0]) : null) ??
      confirmedGeoAutoArm;
    const geoAutoArmPending = fieldPending.isPending('geoAutoArm') || !!pendingGeoAutoArmMT;
    // GeofencePageViewModel.cs's non-Insight (VTracker/Triton) reduced picker
    // pairs value 3 (GEOFENCE_AUTO_ARM_DISARM_ALL_MODES) with "On Security
    // Armed" — NOT value 2 (AWAY_MODE), which is what this previously had.
    // A real Triton device reporting geoAutoArmSetting=3 matched neither
    // option in the old list, so the <select> fell back to its first option
    // ("Disabled") exactly like the string/number mismatch this pass
    // otherwise fixed — same failure mode, different root cause.
    const geoAutoArmOptions = caps.fullGeofenceAutoArmOptions
      ? GEO_AUTO_ARM_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))
      : [
          { value: '0', label: 'Disabled' },
          { value: '3', label: 'On Security Armed' },
        ];

    return (
      <div className="space-y-6">
        <SectionCard title="Geofence Auto Arm/Disarm">
          <div className="divide-y divide-slate-100 dark:divide-navy-800">
            <SettingRow label="Auto Arm/Disarm Mode" description="Automatically change security mode based on geofence status.">
              <div className="flex items-center">
                <SelectDropdown
                  value={String(geoAutoArmValue)}
                  options={geoAutoArmOptions}
                  onChange={(val) =>
                    fieldPending.run('geoAutoArm', Number(val), () =>
                      sendCommand(NAMTMessageType.MT_MESSAGE_SET_GEO_AUTO_ARM_DISARM_MODE, [val]),
                    )
                  }
                />
                {geoAutoArmPending && <PendingBadge />}
              </div>
            </SettingRow>
            {caps.showGeofenceSirenSetting && (
            <SettingRow label="Geofence Siren" description="Sound siren on geofence breach.">
              <div className="flex items-center">
                <ToggleSwitch
                  enabled={fieldPending.getOptimisticValue<boolean>('geoSirenEnabled') ?? (globalSettings.geoSirenEnabled ?? false)}
                  loading={false}
                  onToggle={() =>
                    // MT_MESSAGE_GEOFENCE_SIREN_ENABLED toggles; send with data to enable/disable
                    fieldPending.run('geoSirenEnabled', !globalSettings.geoSirenEnabled, () =>
                      sendCommand(NAMTMessageType.MT_MESSAGE_GEOFENCE_SIREN_ENABLED, [
                        globalSettings.geoSirenEnabled ? '0' : '1',
                      ]),
                    )
                  }
                />
                {(fieldPending.isPending('geoSirenEnabled') || findPendingMT(mtObjList, NAMTMessageType.MT_MESSAGE_GEOFENCE_SIREN_ENABLED)) && (
                  <PendingBadge />
                )}
              </div>
            </SettingRow>
            )}
          </div>
          <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-600 dark:bg-navy-800 dark:text-slate-400">
            Current setting:{' '}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              {resolveGeoAutoArm(confirmedGeoAutoArm)}
            </span>
          </div>
        </SectionCard>
      </div>
    );
  };

  const tabContent: Record<TabKey, () => React.ReactNode> = {
    system: renderSystemSettings,
    geofence: renderGeofenceSync,
  };

  const effectiveTab: TabKey = TABS.some((tab) => tab.key === activeTab) ? activeTab : 'system';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Configuration</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage device settings and preferences</p>
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{feedback.message}</span>
            <button
              type="button"
              onClick={() => setFeedback(null)}
              className="ml-3 text-xs font-medium underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-navy-700">
        <nav className="-mb-px flex space-x-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                effectiveTab === tab.key
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {tabContent[effectiveTab]()}
    </div>
  );
}
